import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { withAsync, Typeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { getSearchedProductList, replacePlaceholderAction } from './store/placeholder.actions';
import { PlaceholderDetailsState } from './store/placeholder.reducer';

const AsyncTypeahead = withAsync(Typeahead);
const ModalHeader = Modal.Header as unknown as React.FC<React.PropsWithChildren<{ className?: string }>>;

type PlaceholderThunkDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

interface ReplaceSelectorState {
  PlaceholderDetails: PlaceholderDetailsState;
}

interface SearchedProduct {
  _id: string;
  number?: string | number;
  seasonId?: { name?: string };
  year?: string | number;
}

interface ReplacePlaceholderState {
  searchValue: string;
  loader: boolean;
  selectedProduct: SearchedProduct | null;
  isChecked: boolean;
  productName?: string;
}

export interface ReplacePlaceholderModalProps {
  isOpen: boolean;
  handleClose: (_value?: boolean) => void;
  devOrderId: string;
}

/**
 * Replace-placeholder modal — searches published products via an async typeahead
 * and swaps the current dev order's placeholder for the selected product once the
 * overwrite warning is acknowledged.
 */
function ReplacePlaceholderModal({ isOpen, handleClose, devOrderId }: ReplacePlaceholderModalProps): React.ReactElement {
  const dispatch = useDispatch<PlaceholderThunkDispatch>();
  const { t } = useTranslation('common');

  const {
    PlaceholderDetails: { searchedProductList },
  } = useSelector((reducer: ReplaceSelectorState) => reducer);

  const [state, setState] = useState<ReplacePlaceholderState>({
    searchValue: '',
    loader: false,
    selectedProduct: null,
    isChecked: false,
  });
  const { searchValue, loader, selectedProduct, isChecked, productName } = state;

  /** Flag the loader when the query is long enough, then stash the raw query. */
  const handleSearch = (value: string) => {
    if (value?.length >= 3) {
      setState((prev) => ({ ...prev, loader: true }));
    }
    setState((prev) => ({ ...prev, searchValue: value }));
  };

  useEffect(() => {
    let delayDebounceFn: ReturnType<typeof setTimeout> | null = null;
    if (searchValue && searchValue.length >= 3) {
      delayDebounceFn = setTimeout(() => {
        dispatch(getSearchedProductList(searchValue));
        setState((prev) => ({ ...prev, loader: false }));
      }, 3000);
    }
    return () => {
      if (delayDebounceFn) {
        clearTimeout(delayDebounceFn);
      }
    };
  }, [searchValue]);

  /** Capture the chosen product and derive its "copy" reference label. */
  const selectProduct = (pro?: SearchedProduct) => {
    setState((prev) => ({
      ...prev,
      selectedProduct: pro ?? null,
      productName: `${pro?.number} copy`,
    }));
  };

  /** Dispatch the replace action and reset local state + close on success. */
  const replacePlacheolderDetails = async () => {
    const reqObj = {
      devOrderId,
      replaceWithDevid: selectedProduct?._id,
    };
    const res = await dispatch(replacePlaceholderAction(reqObj, t));
    if (res?.code === 200) {
      setState((prev) => ({
        ...prev,
        searchValue: '',
        loader: false,
        selectedProduct: null,
        isChecked: false,
        productName: '',
      }));
      handleClose();
    }
  };

  return (
    <Modal
      show={isOpen}
      backdrop="static"
      className="custom-modal replace-placeholder modal-520"
      aria-labelledby="contained-modal-title-vcenter"
      onHide={() => handleClose(false)}
    >
      <ModalHeader>
        <div className="d-flex justify-content-between align-items-center w-100">
          <Modal.Title id="contained-modal-title-vcenter">
            <span>{t('PLACEHOLDER_MANAGEMENT.REPLACE_PLACEHOLDER_MANAGEMENT.MODAL_TITLE')}</span>
          </Modal.Title>
        </div>
      </ModalHeader>
      <Modal.Body>
        <AsyncTypeahead
          promptText="No Libraries found"
          placeholder={t('PLACEHOLDER_MANAGEMENT.REPLACE_PLACEHOLDER_MANAGEMENT.SEARCH_PLACEHOLDER')}
          isLoading={loader}
          labelKey="number"
          autoFocus
          onSearch={(query: string) => handleSearch(query)}
          useCache={false}
          options={(searchedProductList as SearchedProduct[]) || []}
          minLength={1}
          id="typeheadWithLibrary"
          onChange={(selected: SearchedProduct[]) => selectProduct(selected[0])}
          renderMenuItemChildren={(option: SearchedProduct) => (
            <div className="d-flex" key={option._id}>
              <p className="mb-0 f-14">
                {option?.number}
                {option?.seasonId && `, ${option?.seasonId?.name}`}
                {option?.year && `, ${option.year}`}
              </p>
            </div>
          )}
        />
        {productName && (
          <div className="input-group form-floating-label fixed-label mt-2">
            <input type="text" value={productName} readOnly className="form-control f-14" />
            <label className="asterisk-req">{t('PLACEHOLDER_MANAGEMENT.REPLACE_PLACEHOLDER_MANAGEMENT.PRODUCT_REF_LABEL')}</label>
          </div>
        )}
        <div className="card custom-card p-2 mt-3">
          <div className="checkbox-label">
            <input
              type="checkbox"
              name="replace_placeholder"
              id="replace_placeholder"
              onChange={(e) => setState((prev) => ({ ...prev, isChecked: e.target.checked }))}
              checked={isChecked}
            />
            <label htmlFor="replace_placeholder" className="chklabel align-items-start">
              <span>
                {t('PLACEHOLDER_MANAGEMENT.REPLACE_PLACEHOLDER_MANAGEMENT.ATTRIBUTE_OVERWRITE_WARNING')}
                <p className="mt-2 mb-0">{t('PLACEHOLDER_MANAGEMENT.REPLACE_PLACEHOLDER_MANAGEMENT.UNDONE_MESSAGE')}</p>
              </span>
            </label>
          </div>
        </div>
        <div className="modal-buttons mt-5 d-flex justify-content-between">
          <button className="btn btn-default" onClick={() => handleClose()}>
            {t('PLACEHOLDER_MANAGEMENT.REPLACE_PLACEHOLDER_MANAGEMENT.BACK_BUTTON')}
          </button>
          <button disabled={!selectedProduct || !isChecked} onClick={replacePlacheolderDetails} className="btn btn-default">
            {t('PLACEHOLDER_MANAGEMENT.REPLACE_PLACEHOLDER_MANAGEMENT.SAVE_BUTTON')}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ReplacePlaceholderModal;
